import Sidebar from "../../../Components/Sidebar/Sidebar";
import SidebarGroup from "../../../Components/Sidebar/SidebarGroup";
import { Dispatch, SetStateAction, useState } from "react";
import { JobQuery } from "./Jobs";
import { Autocomplete, Checkbox, createFilterOptions, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SidebarForm from "../../../Components/Sidebar/SidebarForm";
import SidebarButton from "../../../Components/Sidebar/SidebarButton";
import useAxiosEffect from "../../../system/useAxiosEffect";
import axios from "axios";
import TextBlock from "../../../Components/Layout/TextBlock";
import formatDateTime from "../../../system/formatDateTime";
import { formatJobStatus } from "./JobListItem";
import { JobStatus, jobStatusList } from "system/Domain";
import { useHistory } from "react-router-dom";
import { AutoFixHigh, Schedule } from "@mui/icons-material";

interface Props {
	query: JobQuery;
	setQuery: Dispatch<SetStateAction<JobQuery>>;
}

interface JobType {
	type: string;
	count: number;
	last: string;
}

export const formatJobType = (type: string) => {
	const tokens = type.split(", ");
	const typeTokens = tokens[0].split(".");
	return typeTokens[typeTokens.length - 1];
};

const typeFilterOptions = createFilterOptions<JobType>({
	stringify: (t) => formatJobType(t.type),
});

export default ({ query, setQuery }: Props) => {
	const history = useHistory();
	const [jobTypes, setJobTypes] = useState<JobType[]>();

	useAxiosEffect(async (config) => {
		const { data } = await axios.get<JobType[]>(`/api/admin/jobs/types`, config);
		setJobTypes(data);
	}, []);

	return (
		<Sidebar>
			<SidebarGroup>
				<SidebarButton
					startIcon={<AutoFixHigh />}
					onClick={() => history.push("/admin/jobs/hub")}
					color="primary"
					label="Jobs Hub"
				/>
				<SidebarButton
					startIcon={<Schedule />}
					onClick={() => history.push("/admin/jobs/cron")}
					color="primary"
					label="Cron Hub"
				/>
			</SidebarGroup>
			<SidebarGroup>
				<SidebarForm>
					<FormControl fullWidth variant="standard">
						<Autocomplete
							multiple
							size="small"
							options={jobTypes ?? []}
							value={(jobTypes ?? []).filter((t) => query.types?.includes(t.type))}
							getOptionLabel={(t) => formatJobType(t.type)}
							isOptionEqualToValue={(option, value) => option.type === value.type}
							filterOptions={typeFilterOptions}
							onChange={(_, value) => {
								setQuery((q) => ({
									...q,
									types: value.map((t) => t.type),
								}));
							}}
							renderOption={(props, t) => (
								<li {...props} key={t.type}>
									<TextBlock reduced primary={formatJobType(t.type)} secondary={formatDateTime(t.last)} />
								</li>
							)}
							renderInput={(params) => <TextField {...params} variant="standard" label="Typen" />}
						/>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Status</InputLabel>
						<Select
							multiple
							multiline
							value={query.states || []}
							renderValue={(v) => (v as JobStatus[])?.map(formatJobStatus).join(", ")}
							onChange={(e) => {
								const value = e.target.value as JobStatus[];
								if (!value) {
									return;
								}

								setQuery((q) => ({
									...q,
									states: value,
								}));
							}}
						>
							{jobStatusList.map((s) => (
								<MenuItem value={s} key={s}>
									{formatJobStatus(s)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									checked={query.onlyRecurring ?? false}
									onChange={(e) =>
										setQuery((q) => ({
											...q,
											onlyRecurring: e.target.checked,
										}))
									}
								/>
							}
							label="Nur Cronjobs"
						/>
					</FormControl>
				</SidebarForm>
			</SidebarGroup>
		</Sidebar>
	);
};
